/**
 * PropertyShareBridge — the landing page for the universal share URL:
 *   /property/:refCode/:id          (with referral)
 *   /property/:id                   (no referral)
 *
 * Behaviour:
 *  1. Persist the refCode (if present) to `pending_referral_code` in
 *     localStorage so any later signup carries the attribution.
 *  2. On a mobile device, attempt to open the native app deep link
 *     `samrumah://property/{id}`. iOS / Android will hand the URL off to
 *     the app if installed.
 *  3. After ~1.2s — long enough for the OS to switch apps if installed —
 *     fall back to the web detail page `/properties/{id}` so a desktop
 *     visitor or a mobile visitor without the app still sees the listing.
 *
 * This component is intentionally a thin bridge: no API calls, no chrome,
 * no flash of unstyled content. A spinner + short copy keeps the user
 * informed if the fallback timer is needed.
 */
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

const APP_SCHEME = "samrumah";
const FALLBACK_DELAY_MS = 1200;

function isMobileUA() {
  if (typeof navigator === "undefined") return false;
  return /android|iphone|ipad|ipod|iemobile|opera mini/i.test(navigator.userAgent || "");
}

export default function PropertyShareBridge() {
  const { refCode, id } = useParams();
  const navigate = useNavigate();
  // When the URL is `/property/:id` (no refCode segment), react-router maps
  // the single param into `refCode`. Detect that and swap.
  const propertyId = id || refCode;
  const referralCode = id ? refCode : null;

  useEffect(() => {
    if (!propertyId) {
      navigate("/properties", { replace: true });
      return;
    }

    // 1. Persist referral attribution.
    if (referralCode) {
      try {
        localStorage.setItem("pending_referral_code", referralCode);
      } catch (_) {
        // Storage may be blocked (private mode, etc.) — non-fatal.
      }
    }

    // 2. On mobile, attempt the deep link.
    let fallbackTimer = null;
    if (isMobileUA()) {
      const deepLink = `${APP_SCHEME}://property/${propertyId}`;
      // Use location.href so iOS Safari treats it as a navigation (window.open
      // is blocked unless invoked from a direct user gesture).
      window.location.href = deepLink;
    }

    // 3. Fall back to the web detail after a short timeout. We always run
    //    the timer so desktop visitors land on the web detail too — just
    //    immediately on desktop (no perceptible delay because the deep
    //    link branch is skipped above).
    fallbackTimer = setTimeout(
      () => {
        // `replace` so the bridge does not stack into browser history.
        navigate(`/properties/${propertyId}`, { replace: true });
      },
      isMobileUA() ? FALLBACK_DELAY_MS : 0,
    );

    return () => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
    // We deliberately depend only on the route params — the navigate
    // reference is stable across renders for react-router v6.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, referralCode]);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 px-6 text-center"
      data-testid="property-share-bridge"
    >
      <Loader2 className="w-10 h-10 text-[#4E50FF] animate-spin mb-4" />
      <p className="text-base font-medium text-slate-800">Opening property…</p>
      <p className="text-sm text-slate-500 mt-1 max-w-xs">
        Launching the SAM Rumah app. If nothing happens, we&apos;ll take you to the
        web version in a moment.
      </p>
    </div>
  );
}
